import { ROUTES } from '@/constants/routes'
import { useDeepAChatSession } from '@/pocs/chat/composables/chat-session'
import { isDeepAEnabled } from '@/utils/helpers/features.ts'
import { isNodeTypeFolder } from '@deepcloud/deep-ui-utils'
import { useDeepBoxAdminStore } from '@/stores/deepbox/admin/admin.ts'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info.ts'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useGlobalStore } from '@/stores/global/global'
import { useSettingsDevStore } from '@/stores/settings/settings-dev'
import { useBoxHiddenNodes } from '@/composables/box/box-hidden-nodes.ts'
import { useNode } from '@/composables/node/node'
import type { DeepAChatContextNode } from '@/pocs/chat/types'

function toContextNode({
  nodeId,
  name,
  displayName,
  mimeType,
  type,
}: DeepAChatContextNode): DeepAChatContextNode {
  return { nodeId, name, displayName, mimeType, type }
}

function isFolder({ type }: DeepAChatContextNode) {
  return !!type && isNodeTypeFolder(type)
}

export function createDeepAChat() {
  const route = useRoute()
  const { getCurrentRootNodeFromRoute } = useNode()
  const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
  const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
  const globalStore = useGlobalStore()
  const settingsDevStore = useSettingsDevStore()
  const { adminBox } = storeToRefs(useDeepBoxAdminStore())
  const { canShowHiddenNodes } = useBoxHiddenNodes(adminBox)

  const isEnabled = computed(
    () => isDeepAEnabled() && settingsDevStore.canModeFeature('DEEP_A'),
  )
  const show = ref(false)
  const started = ref(false)
  const contextNodes = shallowRef<DeepAChatContextNode[]>([])
  const organizationId = ref<string>()

  const session = useDeepAChatSession(organizationId)

  const title = computed(
    () =>
      session.messages.value.find((message) => message.role === 'user')
        ?.content ?? '',
  )

  // the chat isn't offered in the trash, so trashed folders never become its context
  const currentFolder = computed(() =>
    [ROUTES.ROUTE_TRASH, ROUTES.ROUTE_TRASH_NODE].includes(String(route.name))
      ? null
      : getCurrentRootNodeFromRoute(route),
  )

  // DeepA searches folders (and everything below them), never single files
  function captureCurrentContext(
    nodes: DeepAChatContextNode[] = deepBoxDeepBoxesBoxesNodesStore.selectedNodes,
  ): DeepAChatContextNode[] {
    const folders = nodes.filter(isFolder)
    if (folders.length > 0) {
      return folders
    }
    return currentFolder.value ? [currentFolder.value] : []
  }

  const canStartNewChat = computed(() => captureCurrentContext().length > 0)

  const hasMessages = computed(() => session.messages.value.length > 0)

  const otherLocation = computed(() => {
    const folder = currentFolder.value
    if (!hasMessages.value || !folder) return undefined
    const isInContext = contextNodes.value.some(
      (node) => node.nodeId === folder.nodeId,
    )
    return isInContext ? undefined : toContextNode(folder)
  })

  function setContext(nodes: DeepAChatContextNode[]) {
    contextNodes.value = nodes.map(toContextNode)
    organizationId.value = deepBoxBoxInfoStore.accessCompany?.companyId
    started.value = true
  }

  function newChat(nodes?: DeepAChatContextNode[]) {
    session.reset()
    setContext(captureCurrentContext(nodes))
  }

  function send(message: string) {
    if (contextNodes.value.length === 0) return
    session.send(message, {
      deepBoxNodeIds: contextNodes.value.map((node) => node.nodeId),
      includeHidden: canShowHiddenNodes.value,
    })
  }

  function open(nodes?: DeepAChatContextNode[]) {
    if (nodes || !started.value || !hasMessages.value) {
      newChat(nodes)
    }
    globalStore.setBoxDetailsDrawer(false)
    show.value = true
  }

  function close() {
    show.value = false
  }

  function toggle() {
    if (show.value) {
      close()
    } else {
      open()
    }
  }

  watch(
    () => globalStore.boxDetailsDrawer,
    (isDetailsDrawerOpen) => {
      if (isDetailsDrawerOpen) {
        close()
      }
    },
  )

  // until the first question is sent, the context follows the folder being viewed
  watch(
    () => currentFolder.value?.nodeId,
    () => {
      if (show.value && !hasMessages.value && currentFolder.value) {
        setContext([currentFolder.value])
      }
    },
  )

  watch(
    () => route.params.box,
    () => {
      session.reset()
      contextNodes.value = []
      started.value = false
      close()
    },
  )

  onScopeDispose(session.detach)

  return {
    isEnabled,
    show,
    contextNodes,
    messages: session.messages,
    loading: session.loading,
    title,
    canStartNewChat,
    otherLocation,
    open,
    close,
    toggle,
    newChat,
    send,
    stop: session.stop,
  }
}
